import { useState } from 'react';
import { User, ExternalLink, Loader2, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WaitlistEntryOut } from '@/lib/api';

interface WaitlistListProps {
  entries: WaitlistEntryOut[];
  onRemove: (entryId: string) => Promise<void>;
}

export function WaitlistList({ entries, onRemove }: WaitlistListProps) {
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleProfileClick = (userId: string) => {
    window.open(`/profile/${userId}`, '_blank');
  };

  const handleRemove = async (e: React.MouseEvent, entryId: string) => {
    e.stopPropagation();
    setRemovingId(entryId);
    try {
      await onRemove(entryId);
    } finally {
      setRemovingId(null);
    }
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock size={48} className="text-adv-mediumGray mx-auto mb-4" />
        <p className="text-adv-lightGray">No one on the waitlist</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-adv-lightGray text-sm pb-1 border-b border-adv-mediumGray/30">
        <Clock size={14} />
        <span>Waiting for Spot</span>
      </div>
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="flex items-center p-3 border border-adv-mediumGray rounded-lg hover:border-adv-orange transition-colors cursor-pointer group"
          onClick={() => handleProfileClick(entry.user.id)}
        >
          <div className="w-8 h-8 rounded-full bg-adv-orange/20 text-adv-orange flex items-center justify-center mr-3 text-sm font-bold">
            #{entry.position}
          </div>
          <div className="h-10 w-10 rounded-full bg-adv-darkGray flex items-center justify-center mr-3">
            {entry.user.avatar_url ? (
              <img
                src={entry.user.avatar_url}
                alt={entry.user.username}
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <User size={20} className="text-adv-lightGray" />
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-white group-hover:text-adv-orange transition-colors">
              {entry.user.first_name} {entry.user.last_name}
            </p>
            <p className="text-sm text-adv-lightGray">
              Joined {new Date(entry.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30 p-2"
              onClick={(e) => handleRemove(e, entry.id)}
              disabled={removingId === entry.id}
            >
              {removingId === entry.id ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <X size={16} />
              )}
            </Button>
            <ExternalLink size={14} className="text-adv-lightGray group-hover:text-adv-orange transition-colors" />
          </div>
        </div>
      ))}
    </div>
  );
}
